import { useEffect } from "react";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { fetchSupplierInsights } from "../../../store/contractDashboardSlice";
import Sidebar from "../../../components/Sidebar";
import DashboardFilters from "../../../components/ContractDashboard/DashboardFilters";
import InsightsCard from "../../../components/ContractDashboard/InsightsCard";
import MetricsCard from "../../../components/ContractDashboard/MetricsCard";
import SpendWithTop10Suppliers from "../../../components/ContractDashboard/SpendWithTop10Suppliers";
import ICVScoreDistribution from "../../../components/ContractDashboard/ICVScoreDistribution";
import SupplierRiskScore from "../../../components/ContractDashboard/SupplierRiskScore";
import CostCompetitivenessIndex from "../../../components/ContractDashboard/CostCompetitivenessIndex";
import SupplierResponsiveness from "../../../components/ContractDashboard/SupplierResponsiveness";
import OTIFList from "../../../components/ContractDashboard/OTIFList";
import SupplierQualityDefectRate from "../../../components/ContractDashboard/SupplierQualityDefectRate";
import styles from "./SupplierHealth.module.scss";
import PageLoader from "../../../components/PageLoader";

const SupplierHealth = () => {
  const dispatch = useAppDispatch();
  const { supplierInsights, loading, error } = useSelector(
    (state: RootState) => state.contractDashboard,
  );
  const { contractDashboardFilters } = useSelector(
    (state: RootState) => state.filters,
  );

  useEffect(() => {
    const filters = {
      group_of_companies: contractDashboardFilters.groupCompany,
      contract_type: contractDashboardFilters.contractType,
    };

    dispatch(fetchSupplierInsights(filters));
  }, [dispatch, contractDashboardFilters.groupCompany, contractDashboardFilters.contractType]);

  // Show full-page loader while data is being fetched
  if (loading) {
    return <PageLoader label="Loading supplier health..." />;
  }

  if (error) {
    return (
      <Box className={styles.supplierHealthContainer}>
        <Box className={styles.contentWrapper}>
          <Sidebar activeNav="dashboard" />
          <Box className={styles.mainContent}>
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!supplierInsights) {
    return null;
  }

  return (
    <Box className={styles.supplierHealthContainer}>
      <Box className={styles.contentWrapper}>
        <Sidebar activeNav="dashboard" />
        <Box className={styles.mainContent}>
          <Box className={styles.dashboardContainer}>
            <DashboardFilters
              title="Supplier Health"
              subtitle="Reliability, risk, compliance, and strategic alignment of your supplier base."
            />

            <Box className={styles.insightsSection}>
              <Typography variant="h5" className={styles.sectionTitle}>
                Insights
              </Typography>
              <Box className={styles.insightsGrid}>
                <InsightsCard
                  type="risk"
                  title="Supplier Risk Exposure"
                  description={
                    supplierInsights.insights["Supplier Risk Exposure"]
                  }
                />
                <InsightsCard
                  type="bottleneck"
                  title="Performance & Quality Gaps"
                  description={
                    supplierInsights.insights["Performance & Quality Gaps"]
                  }
                />
                <InsightsCard
                  type="leakage"
                  title="Local & Compliance Opportunity"
                  description={
                    supplierInsights.insights["Local & Complaince Oppurtunity"]
                  }
                />
              </Box>
            </Box>

            <Box className={styles.metricsSection}>
              <Box className={styles.metricsGrid}>
                <MetricsCard
                  title="Active Suppliers"
                  value={supplierInsights.supplier_statistics.active_suppliers.count.toString()}
                  percentage={
                    supplierInsights.supplier_statistics.active_suppliers.change
                  }
                  percentageLabel="from previous quarter"
                />
                <MetricsCard
                  title="Suppliers from UAE"
                  value={supplierInsights.supplier_statistics.uae_suppliers.count.toString()}
                  percentage={
                    supplierInsights.supplier_statistics.uae_suppliers.change
                  }
                  percentageLabel="from previous quarter"
                />
                <MetricsCard
                  title="ICV Certified Suppliers"
                  value={supplierInsights.supplier_statistics.icv_certified.count.toString()}
                  percentage={
                    supplierInsights.supplier_statistics.icv_certified.change
                  }
                  percentageLabel="from previous quarter"
                />
                <MetricsCard
                  title="ISO Certified Suppliers"
                  value={supplierInsights.supplier_statistics.iso_certified.count.toString()}
                  percentage={
                    supplierInsights.supplier_statistics.iso_certified.change
                  }
                  percentageLabel="from previous quarter"
                />
              </Box>
            </Box>

            <Box className={styles.chartsSection}>
              <SpendWithTop10Suppliers
                data={supplierInsights.top_suppliers_by_spend}
              />
              <ICVScoreDistribution
                data={supplierInsights.icv_category_distribution}
              />
            </Box>

            <Box className={styles.tableSection}>
              <SupplierRiskScore data={supplierInsights.supplier_risk_scores} />
            </Box>

            <Box className={styles.chartsSection}>
              <CostCompetitivenessIndex
                data={supplierInsights.cost_competitive_index}
              />
              <SupplierResponsiveness
                data={supplierInsights.supplier_responsiveness}
              />
            </Box>

            <Box className={styles.chartsSection}>
              <OTIFList data={supplierInsights.otif_performance} />
              <SupplierQualityDefectRate
                data={supplierInsights.quality_defect_rates}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SupplierHealth;
